// modes.js — S6: per-list mute/block enforcement toggle.
//
// Each canonical list carries ONE enforcement mode chosen by the user:
//   'mute'  -> app.bsky.graph.muteActorList (private client pref)
//   'block' -> app.bsky.graph.listblock record (public)
//   null    -> no enforcement
// Changing mode cleans up the previous one (unmute / delete listblock).

import { LIST_DEFS } from './lists';

export const MODES = ['mute', 'block'];

// ---- Pure helpers (tested) ----

// From existing enforcement signals, derive each canonical list's current
// mode. blockedListblocks: [{ listUri, uri }] (listblock records' subject
// + own uri). mutedListUris: set of list AT-URIs the user has muted.
// Returns [{ name, uri, mode }] for the canonical lists.
export function resolveModes(canonical, blockedListblocks, mutedListUris) {
  const blockedUris = new Set(blockedListblocks.map((b) => b.listUri));

  return canonical.map((l) => ({
    name: l.name,
    uri: l.uri,
    mode: blockedUris.has(l.uri) ? 'block' : mutedListUris.has(l.uri) ? 'mute' : null,
  }));
}

// ---- Agent-bound enforcement ----

// Find the account's existing listblock records: [{ uri, listUri }].
export async function fetchListblocks(agent, did) {
  const out = [];
  let cursor;

  do {
    const res = await agent.com.atproto.repo.listRecords({
      repo: did,
      collection: 'app.bsky.graph.listblock',
      limit: 100,
      cursor,
    });

    for (const rec of res.data.records) {
      out.push({ uri: rec.uri, listUri: rec.value.subject });
    }

    cursor = res.data.cursor;
  } while (cursor);

  return out;
}

async function ensureNoListblock(agent, did, listUri) {
  const existing = await fetchListblocks(agent, did);

  for (const b of existing) {
    if (b.listUri === listUri) {
      await agent.com.atproto.repo.deleteRecord({
        repo: did,
        collection: 'app.bsky.graph.listblock',
        rkey: b.uri.split('/').pop(),
      });

      return;
    }
  }
}

async function ensureListblock(agent, did, listUri) {
  const existing = await fetchListblocks(agent, did);

  if (existing.some((b) => b.listUri === listUri)) return;
  await agent.com.atproto.repo.createRecord({
    repo: did,
    collection: 'app.bsky.graph.listblock',
    record: { subject: listUri, createdAt: new Date().toISOString() },
  });
}

// Apply a mode to one list. Setting a mode removes the OTHER mode's
// enforcement; mode null removes both. Returns the resulting mode.
export async function setListMode(agent, did, listUri, mode) {
  if (mode === 'mute') {
    await ensureNoListblock(agent, did, listUri);
    await agent.app.bsky.graph.muteActorList({ list: listUri });
  } else if (mode === 'block') {
    await agent.app.bsky.graph.unmuteActorList({ list: listUri });
    await ensureListblock(agent, did, listUri);
  } else {
    await ensureNoListblock(agent, did, listUri);
    await agent.app.bsky.graph.unmuteActorList({ list: listUri });
  }

  return mode;
}

// Read current enforcement state fresh from the network, keyed by list
// URI, for the account's canonical lists (must already exist).
export async function fetchModes(agent, did, canonical) {
  const listblocks = await fetchListblocks(agent, did);
  // muted list URIs come from getListMutes (paginated).
  const muted = new Set();
  let cursor;

  do {
    const res = await agent.app.bsky.graph.getListMutes({ limit: 100, cursor });

    for (const m of res.data.list) {
      if (m.viewer?.muted) muted.add(m.uri);
    }

    cursor = res.data.cursor;
  } while (cursor);

  return resolveModes(canonical, listblocks, muted);
}

export { LIST_DEFS };
